import { riskZonesGreenToRed } from "./risk_colors";
import * as Highcharts from "highcharts";

export type RiskThresholds = { low: number; medium: number; high: number };

// Charts attach a custom `url` to point options so a click can navigate to a detail page.
declare module "highcharts" {
  interface PointOptionsObject {
    url?: string;
  }
}

// Highcharts types a tooltip formatter's `this` as a bare Point, but at runtime it also
// exposes the surrounding context: the hovered point, its series, and the category key.
interface ChartTooltipContext {
  key?: number | string;
  point: Highcharts.Point;
  series: Highcharts.Series;
}

// Chart components always emit data-risk-thresholds (defaulting to the Tier 1 scoring
// strategy's thresholds), so the authoritative default lives in Ruby, not here.
export function readRiskThresholds(element: HTMLElement): RiskThresholds {
  return JSON.parse(element.dataset.riskThresholds!) as RiskThresholds;
}

// Highcharts' types only accept a ColorType, not null; "transparent" keeps the
// background see-through (matching the previous null behavior) while satisfying them.
export const chartBackgroundColor = "transparent";
export const lineColor = "#999492";
export const riskZoneLineColor = "#dadada";
export const primaryGraphColor = "#424343";
export const gridLineColor = lineColor;
export const crosshairColor = "rgba(209, 236, 255, .66)";
export const tierFillColor = "#60a5fa"; // Tailwind blue-400
export const tierBackgroundColor = "#dbeafe"; // Tailwind blue-100

function riskLabeler(crp_value: number, thresholds: RiskThresholds) {
  if (crp_value >= thresholds.high) {
    return "High Risk";
  } else if (crp_value >= thresholds.medium) {
    return "Medium Risk";
  } else {
    return "Low Risk";
  }
}

export function portfolioColumnTooltipFormatter(
  context: Highcharts.Point,
  thresholds: RiskThresholds,
) {
  const column = context as unknown as ChartTooltipContext;
  const i = column.series.data.indexOf(column.point);
  const risk_column = column.series.chart.series[1].points[i];
  return (
    "<span>" +
    column.key +
    "</span><br/>" +
    '<span style="color:' +
    risk_column.color +
    '">\u25CF</span><b> ' +
    riskLabeler(risk_column.y!, thresholds) +
    "</b><br/>"
  );
}

export function columnTooltipFormatter(
  context: Highcharts.Point,
  resourceNames: string[],
  comparativeCrpData: number[],
  scores: number[],
  thresholds: RiskThresholds,
) {
  const column = context as unknown as ChartTooltipContext;
  const i = column.series.data.indexOf(column.point);
  const risk_column = column.series.chart.series[1].points[i];
  const bullet = "<span>\u25CF</span><b>";
  const score =
    bullet +
    "<span>" +
    " Score: " +
    (100 - Math.round(scores[i])) +
    "</span><br/>";
  const averageScore = comparativeCrpData
    ? "<span>\u25CF</span><b>" +
      "<span>" +
      " Average Score: " +
      (100 - Math.round(comparativeCrpData[i])) +
      "</span><br/>"
    : "";
  return (
    "<span>" +
    resourceNames[i] +
    "</span><br/>" +
    '<span style="color:' +
    risk_column.color +
    '">\u25CF</span><b> ' +
    riskLabeler(risk_column.y!, thresholds) +
    "</b><br/>" +
    score +
    averageScore
  );
}

export function controlCategoryDataLabelFormatter(
  series: Highcharts.Point,
  average_score: number | false,
) {
  const average_score_text =
    average_score == false
      ? ""
      : "<span style='color: #999;font-size: .8em;font-weight: bold;'>AVG: " +
        average_score +
        "%</span>";
  return `
    <div class='text-gray-700 text-center whitespace-normal'>
      <div class='font-semibold text-xl'>
        ${series.y}%
      </div>
      <div class='text-lg'>controls complete</div>
      <div>${average_score_text}</div>
    </div>
  `;
}

export function positionDial(crp_value: number) {
  return (crp_value / 100) * 90;
}

function setLabelFontSize() {
  return "10.5px";
}

export const chartFont = "helvetica";
export const xAxisTickWidth = 0;
export const labelFontSize = setLabelFontSize();
export const labelTextOverflow = "none";

export function crpBarChartYAxisOptions(
  thresholds: RiskThresholds,
): Highcharts.YAxisOptions {
  // Label positions sit at the midpoint of each bucket so they appear centered between
  // the boundary lines on the y-axis.
  const lowMid = (thresholds.low + thresholds.medium) / 2;
  const mediumMid = (thresholds.medium + thresholds.high) / 2;
  const highMid = (thresholds.high + 100) / 2;
  const labels: Record<number, string> = {
    [lowMid]: "Low Risk",
    [mediumMid]: "Medium Risk",
    [highMid]: "High Risk",
  };
  return {
    plotBands: [
      {
        color: riskZoneLineColor,
        from: thresholds.medium - 0.2,
        to: thresholds.medium + 0.1,
      },
      {
        color: riskZoneLineColor,
        from: thresholds.high - 0.2,
        to: thresholds.high + 0.1,
      },
    ],
    lineWidth: 1,
    lineColor: lineColor,
    gridLineWidth: 0,
    gridLineColor: lineColor,
    opposite: true,
    tickPositions: [-2, 0, lowMid, mediumMid, highMid, 100],
    title: {
      text: undefined,
    },
    labels: {
      style: {
        color: primaryGraphColor,
        fontSize: labelFontSize,
      },
      formatter: function (
        this: Highcharts.AxisLabelsFormatterContextObject,
      ): string {
        // Only the bucket-midpoint ticks carry a risk label; other ticks render blank.
        return labels[this.value as number] ?? "";
      },
    },
  };
}

export const xAxisLabelStyle = {
  fontSize: labelFontSize,
  color: primaryGraphColor,
  textOverflow: labelTextOverflow,
};

export const plotOptionsBarBorderColor = "transparent";
export const plotOptionsBarBorderWidth = 0;
export const plotOptionsBarGrouping = false;
export const plotOptionsBarShowInLegend = false;
export const plotOptionsBarThreshold = -2;
export const plotOptionsBarZones = riskZonesGreenToRed;
export const barChartBorderRadius = 3;
